import { useState, type FormEvent, type ReactNode } from "react";
import { can, PREVIOUS_HEATING_FUELS, type PreviousHeatingFuel } from "@sts/schemas";
import type { Contact, Me, Property } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import { isoDay } from "../lib/format.js";

/**
 * The customer book: the people and the houses.
 *
 * Both lists are organisation-wide, so the API refuses them outright to a role
 * scoped to its own records — a surveyor sees the customer of a job they are
 * booked onto and no others. That refusal is rendered as what it is: `Async`
 * names the grant that was withheld and offers no "Try again", because retrying
 * a 403 cannot succeed and a button that never works is worse than no button.
 *
 * There is no per-contact screen, and that is deliberate rather than unfinished.
 * A contact has four fields and every one of them is in the table below, so a
 * detail page would repeat the row it was opened from. The screen worth having
 * is "the jobs for this customer", and the API has no route that answers it —
 * `jobs` cannot be queried by `contact_id`. Adding a page that could not answer
 * the only question it would be opened to answer is not an improvement.
 */

interface Book {
  readonly contacts: readonly Contact[];
  readonly properties: readonly Property[];
  readonly me: Me;
}

export function Contacts(): ReactNode {
  const { client } = usePortal();

  const { state, reload } = useAsync<Book>(async () => {
    // Awaited together rather than settled separately: both lists sit behind the
    // same `contact:read` grant, so a refusal of one is a refusal of both, and
    // half a customer book is not a useful thing to render.
    const [contacts, properties, me] = await Promise.all([
      client.contacts(),
      client.properties(),
      client.me(),
    ]);
    return { contacts, properties, me };
  }, [client]);

  return (
    <>
      <div className="page-header spread">
        <h1>Customers</h1>
        <span className="small">
          <a href={href.newLead()}>New lead</a>
        </span>
      </div>

      <Async state={state} what="the customer book" reload={reload}>
        {(book) => <CustomerBook book={book} reload={reload} />}
      </Async>
    </>
  );
}

function CustomerBook({
  book,
  reload,
}: {
  readonly book: Book;
  readonly reload: () => void;
}): ReactNode {
  // Asked of the same matrix the API enforces, rather than guessed from the role
  // name. A form nobody may submit is only a slower way of being refused.
  const mayCreate = can({
    role: book.me.role,
    resource: "contact",
    action: "create",
  });

  return (
    <>
      <section>
        <h2>
          People <span className="count">{book.contacts.length}</span>
        </h2>
        {book.contacts.length === 0 ? (
          <p className="empty">
            No contacts yet. A contact is the person a job belongs to — the name
            on the quotation and on the MCS certificate.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                Contacts held by this organisation
              </caption>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Telephone</th>
                  <th scope="col">Added</th>
                </tr>
              </thead>
              <tbody>
                {book.contacts.map((contact) => (
                  <tr key={contact.id}>
                    <th scope="row">{contact.fullName}</th>
                    <td>{contact.email ?? <Absent />}</td>
                    <td>{contact.phone ?? <Absent />}</td>
                    <td className="ts">{isoDay(contact.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {mayCreate ? <NewContact reload={reload} /> : null}
      </section>

      <section>
        <h2>
          Properties <span className="count">{book.properties.length}</span>
        </h2>
        {book.properties.length === 0 ? (
          <p className="empty">
            No properties yet. A property is the dwelling being surveyed, and its
            age band is what the survey falls back on when nothing better is
            known about a wall.
          </p>
        ) : (
          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">
                Properties held by this organisation
              </caption>
              <thead>
                <tr>
                  <th scope="col">Address</th>
                  <th scope="col">Postcode</th>
                  <th scope="col">Age band</th>
                  <th scope="col">EPC</th>
                  <th scope="col">Previous heating fuel</th>
                </tr>
              </thead>
              <tbody>
                {book.properties.map((property) => (
                  <tr key={property.id}>
                    <th scope="row">{addressOf(property)}</th>
                    <td className="mono">{property.postcode}</td>
                    <td>{property.ageBand ?? <Absent />}</td>
                    <td className="mono small">
                      {property.epcReference ?? <Absent />}
                    </td>
                    <td>
                      {property.previousHeatingFuel !== null
                        ? PREVIOUS_HEATING_FUEL_LABEL[property.previousHeatingFuel]
                        : <Absent />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {mayCreate ? <NewProperty reload={reload} /> : null}
      </section>

      {mayCreate ? null : (
        <div className="notice" role="note">
          <p className="small">
            Your role may read the customer book but not add to it, so the forms
            for a new contact and a new property are not shown.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * How the previous heating fuel reads on screen — the BUS off gas grid uplift
 * (§3.48) pays the extra £9,000 only for oil or LPG, so the label says that
 * inline rather than leaving a surveyor to remember which values qualify.
 */
const PREVIOUS_HEATING_FUEL_LABEL: Record<PreviousHeatingFuel, string> = {
  oil: "Oil (qualifies for the off gas grid uplift)",
  lpg: "LPG (qualifies for the off gas grid uplift)",
  "mains-gas": "Mains gas",
  electric: "Electric (e.g. storage heaters)",
  other: "Other",
  "unknown-or-absent": "Asked — not confirmed, or no previous system",
};

/** One line, the way it would be written on an envelope. */
export function addressOf(property: Property): string {
  return [property.addressLine1, property.addressLine2, property.town]
    .filter((part): part is string => part !== null && part.trim().length > 0)
    .join(", ");
}

/**
 * Nothing recorded, shown as a word rather than an empty cell.
 *
 * A blank cell in a table reads as a rendering fault; the reader cannot tell it
 * from a column that failed to load.
 */
function Absent(): ReactNode {
  return <span className="muted">Not recorded</span>;
}

function NewContact({ reload }: { readonly reload: () => void }): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      await client.createContact({
        fullName,
        // Sent only when filled in. The API treats a blank string as "nothing
        // recorded" anyway, but saying nothing is clearer than saying "".
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
      });
      setFullName("");
      setEmail("");
      setPhone("");
      reload();
    });
  }

  return (
    <>
      <h3>Add a contact</h3>
      <form className="stacked" onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="contact-name">Full name</label>
          <input
            id="contact-name"
            type="text"
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="contact-email">Email</label>
          <input
            id="contact-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="contact-phone">Telephone</label>
          <input
            id="contact-phone"
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <button type="submit" disabled={busy || fullName.trim() === ""}>
          {busy ? "Saving…" : "Add contact"}
        </button>
      </form>
      {error !== null ? <Failure error={error} what="that contact" /> : null}
    </>
  );
}

function NewProperty({ reload }: { readonly reload: () => void }): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [town, setTown] = useState("");
  const [postcode, setPostcode] = useState("");
  const [ageBand, setAgeBand] = useState("");
  const [previousHeatingFuel, setPreviousHeatingFuel] = useState<
    PreviousHeatingFuel | ""
  >("");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await submit(async () => {
      await client.createProperty({
        addressLine1: line1,
        postcode,
        ...(line2.trim() ? { addressLine2: line2.trim() } : {}),
        ...(town.trim() ? { town: town.trim() } : {}),
        ...(ageBand.trim() ? { ageBand: ageBand.trim() } : {}),
        ...(previousHeatingFuel !== "" ? { previousHeatingFuel } : {}),
      });
      setLine1("");
      setLine2("");
      setTown("");
      setPostcode("");
      setAgeBand("");
      setPreviousHeatingFuel("");
      reload();
    });
  }

  return (
    <>
      <h3>Add a property</h3>
      <form className="stacked" onSubmit={(e) => void onSubmit(e)}>
        <div className="field">
          <label htmlFor="property-line1">Address line 1</label>
          <input
            id="property-line1"
            type="text"
            required
            value={line1}
            onChange={(e) => setLine1(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="property-line2">Address line 2</label>
          <input
            id="property-line2"
            type="text"
            value={line2}
            onChange={(e) => setLine2(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="property-town">Town</label>
          <input
            id="property-town"
            type="text"
            value={town}
            onChange={(e) => setTown(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="property-postcode">Postcode</label>
          <input
            id="property-postcode"
            type="text"
            required
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="property-age-band">Age band</label>
          <input
            id="property-age-band"
            type="text"
            value={ageBand}
            onChange={(e) => setAgeBand(e.target.value)}
          />
          <p className="hint small">
            RdSAP construction band, e.g. 1919-1944. Used for assumed U-values
            where the survey has nothing better.
          </p>
        </div>
        <div className="field">
          <label htmlFor="property-previous-heating-fuel">
            Previous heating fuel
          </label>
          <select
            id="property-previous-heating-fuel"
            value={previousHeatingFuel}
            onChange={(e) =>
              setPreviousHeatingFuel(e.target.value as PreviousHeatingFuel | "")
            }
          >
            <option value="">Not yet asked</option>
            {PREVIOUS_HEATING_FUELS.map((fuel) => (
              <option key={fuel} value={fuel}>
                {PREVIOUS_HEATING_FUEL_LABEL[fuel]}
              </option>
            ))}
          </select>
          <p className="hint small">
            The fuel of the system this heat pump replaces. The BUS off gas
            grid uplift (Installer Guidance V5.1 §3.48) pays an extra £9,000
            only when this was oil or LPG — leaving it as "Not yet asked"
            withholds the uplift the same way an unconfirmed answer does, but
            only an explicit answer here is evidence the question was put.
          </p>
        </div>
        <button type="submit" disabled={busy || line1.trim() === "" || postcode.trim() === ""}>
          {busy ? "Saving…" : "Add property"}
        </button>
      </form>
      {error !== null ? <Failure error={error} what="that property" /> : null}
    </>
  );
}
